import { useState } from "react";
import type { CSSProperties } from "react";

import s from "./Feedback.module.scss";
import Container from "../../layout/Container/Container";
import api from "api/axiosInstance";
import { useClinicInfo } from "api/queries/useClinicInfo";
import { toMap } from "app/clinicInfoMap";
import Modal from "components/layout/Modal/Modal";

import { ReactComponent as PhoneIcon } from "../../../assets/ui/phone.svg";
import { ReactComponent as PinIcon } from "../../../assets/ui/pin.svg";
import { ReactComponent as ClockIcon } from "../../../assets/ui/clock.svg";
import { ReactComponent as RouteIcon } from "../../../assets/ui/route.svg";

type SocialItem = {
  key: string;
  href: string;
  label: string;
  iconSrc: string;
};

function getPhones(cfg: Record<string, string | undefined>) {
  const phones: string[] = [];

  const raw = cfg["contacts.phones"];
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((x) => {
          if (typeof x === "string" && x.trim()) phones.push(x.trim());
        });
      }
    } catch {
      // ignore
    }
  }

  if (phones.length === 0 && cfg["contacts.phone"]?.trim()) {
    phones.push(cfg["contacts.phone"].trim());
  }

  if (phones.length === 0) {
    phones.push("+7 (000) 000-00-00");
  }

  return Array.from(new Set(phones));
}

function normalizePhoneHref(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("8")) return `+7${digits.slice(1)}`;
  if (digits.startsWith("7")) return `+${digits}`;
  if (digits.length === 10) return `+7${digits}`;
  return `+${digits}`;
}

function buildYandexQueryLink(address: string) {
  const q = encodeURIComponent(address || "стоматология");
  return `https://yandex.ru/maps/?text=${q}`;
}

function normalizeExternalLink(href: string) {
  const trimmed = href.trim();
  if (!trimmed) return "";

  if (/^(https?:)?\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function getIconMaskStyle(iconSrc: string): CSSProperties {
  return {
    WebkitMaskImage: `url(${iconSrc})`,
    maskImage: `url(${iconSrc})`,
  };
}

export default function Feedback() {
  const { data, isLoading, isError } = useClinicInfo();
  const cfg = toMap(data ?? []) as Record<string, string | undefined>;

  const phones = getPhones(cfg);
  const address = cfg["contacts.address"] || "Технические неполадки";
  const hours = cfg["contacts.hours"] || "Технические неполадки";

  const mapLinkFromDb = (cfg["contacts.mapLink"] || "").trim();
  const mapLink = mapLinkFromDb || buildYandexQueryLink(address);

  const socialItems: SocialItem[] = [
    {
      key: "vk",
      href: (cfg["contacts.social.vk"] || "").trim(),
      label: "VK",
      iconSrc: "/icons/vk.svg",
    },
    {
      key: "max",
      href: (cfg["contacts.social.max"] || "").trim(),
      label: "MAX",
      iconSrc: "/icons/max.svg",
    },
  ].filter((item) => Boolean(item.href));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const limited = digits.substring(0, 11);

    let result = "+7";

    if (limited.length > 1) result += "(" + limited.substring(1, 4);
    if (limited.length >= 4) result += ")" + limited.substring(4, 7);
    if (limited.length >= 7) result += "-" + limited.substring(7, 9);
    if (limited.length >= 9) result += "-" + limited.substring(9, 11);

    return result;
  };

  const [form, setForm] = useState({
    title: "",
    description: "",
    phone: "",
    email: "",
  });

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.phone.trim() ||
      !form.email.trim()
    ) {
      setModalMessage("Пожалуйста, заполните все поля.");
      setModalOpen(true);
      return;
    }

    if (!emailRegex.test(form.email)) {
      setModalMessage("Пожалуйста, введите корректный email.");
      setModalOpen(true);
      return;
    }

    try {
      await api.post("/feedback", form);
      setModalMessage("Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.");
      setModalOpen(true);

      setForm({
        title: "",
        description: "",
        phone: "",
        email: "",
      });
    } catch (error) {
      console.error("Ошибка при отправке:", error);
      setModalMessage("Произошла ошибка при отправке. Пожалуйста, попробуйте позже.");
      setModalOpen(true);
    }
  };

  return (
    <>
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div style={{ padding: 20 }}>
          <h3>Сообщение</h3>
          <p>{modalMessage}</p>
          <button onClick={() => setModalOpen(false)}>Ок</button>
        </div>
      </Modal>

      <div className={s.section}>
        <Container>
          <div className={s.containerFeedback}>
            <div className={s.containerContact}>
              <div className={s.content}>
                <h2>Контакты</h2>

                {isLoading ? (
                  <div className={s.p}>Загрузка...</div>
                ) : isError ? (
                  <div className={s.p}>Нет связи с сервером</div>
                ) : (
                  <div className={s.contentItem}>
                    <div className={s.contactBlock}>
                      <div className={s.contactRowTitle}>
                        <PhoneIcon className={s.contactIcon} aria-hidden="true" />
                        <span>Телефоны</span>
                      </div>

                      <div className={s.contactPhones}>
                        {phones.map((phone) => (
                          <a
                            key={phone}
                            href={`tel:${normalizePhoneHref(phone)}`}
                            className={s.contactLink}
                          >
                            {phone}
                          </a>
                        ))}
                      </div>
                    </div>

                    <div className={s.contactBlock}>
                      <div className={s.contactRowTitle}>
                        <PinIcon className={s.contactIcon} aria-hidden="true" />
                        <span>Адрес</span>
                      </div>

                      <p className={s.contactText}>{address}</p>
                    </div>

                    <div className={s.contactBlock}>
                      <div className={s.contactRowTitle}>
                        <ClockIcon className={s.contactIcon} aria-hidden="true" />
                        <span>Режим работы</span>
                      </div>

                      <p className={s.contactText}>{hours}</p>
                    </div>

                    <div className={s.contactBlock}>
                      <div className={s.contactRowTitle}>
                        <RouteIcon className={s.contactIcon} aria-hidden="true" />
                        <span>Как добраться</span>
                      </div>

                      <a
                        href={mapLink}
                        target="_blank"
                        rel="noreferrer"
                        className={s.contactLink}
                      >
                        Открыть в Яндекс.Картах
                      </a>
                    </div>

                    {socialItems.length > 0 && (
                      <div className={s.socials} aria-label="Социальные сети">
                        {socialItems.map((item) => (
                          <a
                            key={item.key}
                            href={normalizeExternalLink(item.href)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={item.label}
                            title={item.label}
                            className={s.socialLink}
                          >
                            <span
                              className={`${s.socialIcon} ${s[`socialIcon_${item.key}`]}`}
                              style={getIconMaskStyle(item.iconSrc)}
                              aria-hidden="true"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={s.containerForm}>
              <h2>Обратная связь</h2>

              <div className={s.containerInput}>
                <input
                  name="title"
                  value={form.title}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  placeholder="Тема вашего обращения"
                  type="text"
                />

                <textarea
                  name="description"
                  value={form.description}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  placeholder="Опишите проблему"
                />

                <input
                  name="phone"
                  value={form.phone}
                  onChange={(e) => {
                    const formatted = formatPhone(e.target.value);
                    handleChange(e.target.name, formatted);
                  }}
                  placeholder="+7(___) ___-__-__"
                  type="text"
                />

                <input
                  name="email"
                  value={form.email}
                  onChange={(e) => {
                    const filtered = e.target.value.replace(/[^a-zA-Z0-9@._-]/g, "");
                    handleChange(e.target.name, filtered);
                  }}
                  placeholder="Адрес электронной почты"
                  type="text"
                />
              </div>

              <button onClick={handleSubmit}>Отправить</button>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}
